import { useEffect, useMemo, useRef, useState } from "react";
import { Airport } from "@/models/airport";
import { searchAirports } from "@/daily/searchAirports";
import { dt, locale } from "@/daily/strings";
import { DailyPuzzle } from "@/daily/types";
import { FitText } from "./FitText";
import { ClockIcon, PlaneIcon } from "./icons";

interface GuessCardProps {
  puzzle: DailyPuzzle;
  airports: Airport[];
  // Airports already guessed are left out of the suggestions.
  guessedIatas: string[];
  // The guess being checked on the map. Its code sits in the TO box, and
  // guessing is paused, until the result is in.
  checkingIata: string | null;
  onGuess: (airport: Airport) => void;
}

// The floating card the player guesses from: the flight info row, the FROM
// and TO boxes, and the Guess button. Airport suggestions open above it.
export const GuessCard = ({
  puzzle,
  airports,
  guessedIatas,
  checkingIata,
  onGuess,
}: GuessCardProps) => {
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [shaking, setShaking] = useState(false);
  // Which suggestion is highlighted; Enter and the Guess button pick it.
  const [activeIndex, setActiveIndex] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);

  const suggestions = useMemo(
    () =>
      searchAirports(
        value,
        airports.filter(
          (airport) =>
            airport.iata !== puzzle.origin.iata &&
            !guessedIatas.includes(airport.iata)
        )
      ),
    [value, airports, guessedIatas, puzzle]
  );

  // A new search starts from the top match again.
  useEffect(() => setActiveIndex(0), [value]);

  // Keep the highlighted row in view when the list scrolls.
  useEffect(() => {
    list.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Escape") {
      setValue("");
    }
  };

  const pick = (airport: Airport) => {
    setValue("");
    input.current?.blur();
    onGuess(airport);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (checkingIata) return;
    const chosen = suggestions[activeIndex] ?? suggestions[0];
    if (chosen) {
      pick(chosen);
      return;
    }
    // Nothing matches what was typed: shake the TO box, as the classic game does.
    setShaking(true);
    input.current?.focus();
  };

  // The box holds about four and a half letters of the prompt at full size.
  const promptFit = Math.min(1, 4.5 / dt("toPlaceholder").length);

  return (
    <>
      {suggestions.length > 0 && (
        <ul
          ref={list}
          id="ds-suggestions"
          className="ds-suggestions"
          role="listbox"
          aria-label={dt("suggestions")}
        >
          {suggestions.map((airport, index) => (
            <li
              key={airport.iata}
              id={`ds-suggestion-${index}`}
              role="option"
              aria-selected={index === activeIndex}
            >
              <button
                type="button"
                className={`ds-suggestion ${index === activeIndex ? "ds-suggestion--active" : ""}`}
                tabIndex={-1}
                onMouseEnter={() => setActiveIndex(index)}
                // Keep focus in the input so the keyboard does not close first.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(airport)}
              >
                <FitText max={22} min={9} className="ds-suggestion__name">
                  {airport.name}
                </FitText>
                <span className="ds-suggestion__meta">
                  {airport.country_emoji}{" "}
                  {airport.daily_departures === 1
                    ? dt("dailyFlightsOne")
                    : dt("dailyFlights", { count: airport.daily_departures.toLocaleString(locale()) })}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className={`ds-card ${focused ? "ds-card--typing" : ""}`}
        onSubmit={submit}
      >
        <div className="ds-info">
          <div className="ds-info__item">
            <span className="ds-info__icon">
              <span style={{ display: "flex", transform: "rotate(-90deg)" }}>
                <PlaneIcon size={16} />
              </span>
            </span>
            <span className="ds-info__text">
              <span className="ds-label">{puzzle.aircraftMaker}</span>
              <FitText max={22} className="ds-info__value">
                {puzzle.aircraftModel}
              </FitText>
            </span>
          </div>
          <div className="ds-info__item">
            <span className="ds-info__icon">
              <ClockIcon size={16} />
            </span>
            <span className="ds-info__text">
              <span className="ds-label">{dt("departed", { iata: puzzle.origin.iata })}</span>
              <FitText max={22} className="ds-info__value">
                {puzzle.departureTimeLocal}
              </FitText>
            </span>
          </div>
        </div>

        <hr className="ds-rule ds-card__rule" />

        <div className="ds-route">
          <div className="ds-route__box ds-route__box--from">
            <span className="ds-label">{dt("from")}</span>
            <span className="ds-route__code">{puzzle.origin.iata}</span>
          </div>
          <span className="ds-route__badge">
            <PlaneIcon size={14} />
          </span>
          <div
            className={`ds-route__box ds-route__box--to ${shaking ? "ds-shake" : ""}`}
            onAnimationEnd={() => setShaking(false)}
          >
            <label className="ds-label ds-label--red" htmlFor="ds-destination">
              {dt("to")}
            </label>
            <input
              ref={input}
              id="ds-destination"
              className="ds-route__input"
              disabled={!!checkingIata}
              type="text"
              placeholder={dt("toPlaceholder")}
              style={{ "--ds-prompt-fit": promptFit } as React.CSSProperties}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="go"
              role="combobox"
              aria-expanded={suggestions.length > 0}
              aria-controls="ds-suggestions"
              aria-autocomplete="list"
              aria-activedescendant={
                suggestions.length > 0 ? `ds-suggestion-${activeIndex}` : undefined
              }
              onKeyDown={onKeyDown}
              value={checkingIata ?? value}
              onChange={(event) => setValue(event.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
            />
          </div>
        </div>

        <button
          type="submit"
          className={`ds-button ds-button--red ${checkingIata ? "ds-button--busy" : ""}`}
          disabled={!!checkingIata}
        >
          {checkingIata ? (
            <>
              <span className="ds-button__spinner" aria-hidden="true" />
              {dt("checking", { iata: checkingIata })}
            </>
          ) : (
            dt("guess")
          )}
        </button>
      </form>
    </>
  );
};
